import React, { Suspense, lazy, useEffect } from "react";
import { ToastContainer } from 'react-toastify';
import { HashRouter as Router, Route, Routes, Link } from 'react-router-dom';
import "./App.css";
import 'react-toastify/dist/ReactToastify.css';
import "bootstrap/dist/css/bootstrap.min.css";
import ErrorBoundary from "./components/ErrorBoundary";
import Loading from "./components/Loading";
import Header from "./components/Header";
import Inputcontainer from "./components/Inputcontainer.js";
import ErrorBanner from "./components/ErrorBanner";
import useSchemaStore from "./store/schemaStore";
import QueryBuilder from "./components/QueryBuilder.js";

// Lazy load route components for code splitting
const About = lazy(() => import('./components/AboutSection'));
const Quiz = lazy(() => import('./components/Quiz'));
const DataTypes = lazy(() => import('./components/DataTypesSection'));
const Outputcontainer = lazy(() => import('./components/Outputcontainer.js'));

function App() {
  // Get state and actions from Zustand store (subscribe to changes)
  const store = useSchemaStore();
  const {
    fields,
    tableName,
    tableComment,
    tableEngine,
    tableResource,
    migrateTable,
    foreignKeys,
    uniqueKeys,
    indices,
    queryBuilder,
    mysqlQuery,
    queryError,
    darkMode,
    // Actions
    generateAllOutputs,
    parseQuery,
  } = store;

  // Initialize dark mode on first mount
  useEffect(() => {
    // Apply theme to document on mount and whenever darkMode changes
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  // Trigger output generation when schema changes
  useEffect(() => {
    generateAllOutputs();
  }, [
    fields,
    tableName,
    tableComment,
    tableEngine,
    tableResource,
    migrateTable,
    foreignKeys,
    uniqueKeys,
    indices,
    generateAllOutputs,
  ]);

  // Trigger query parsing when in query builder mode
  useEffect(() => {
    if (queryBuilder && mysqlQuery) {
      parseQuery();
    }
  }, [queryBuilder, mysqlQuery, parseQuery]);

  // Compute whether to display outputs
  const shouldDisplaySchemaOutput = tableName && fields.some(field => field.name) &&
    (!queryBuilder || (mysqlQuery && !queryError));

  const shouldDisplaySqlOutput = shouldDisplaySchemaOutput && !queryBuilder;

  return (
    <ErrorBoundary>
      <Router>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/about" element={<About />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/datatypes" element={<DataTypes />} />
            <Route path="/" element={
              <div className="container mt-5">
                <ErrorBanner />
                <Header />
                {!queryBuilder && <Inputcontainer />}
                {queryBuilder && (
                  <QueryBuilder />
                )}
                <hr />
                <Suspense fallback={<div>Loading outputs…</div>}>
                  <Outputcontainer
                    shouldDisplaySchemaOutput={shouldDisplaySchemaOutput}
                    shouldDisplaySqlOutput={shouldDisplaySqlOutput}
                  />
                </Suspense>
              </div>
            } />
          </Routes>
        </Suspense>
        <div className="footer">
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
          <Link to="/datatypes">MySQL DataTypes</Link>
          <Link to="/quiz">Quiz</Link>
          <a href="/system-config-generator">System Config Generator</a>
        </div>
        <ToastContainer hideProgressBar={true} autoClose={1900} theme="dark" />
      </Router>
    </ErrorBoundary>
  );
}

export default App;